import type { GuideOptions } from "@/components/Guide/Guide";

import dynamic from "next/dynamic";
import { useState } from "react";
import { createSharedContext } from "./createSharedContext";

export const newRegisterGuideStore = {
  get(key: string) {
    const value = localStorage.getItem("newRegisterGuide") || "{}";
    return JSON.parse(value)[key] || false;
  },
  set(key: string, value: number | string | boolean) {
    const currentData = JSON.parse(
      localStorage.getItem("newRegisterGuide") || "{}"
    );
    currentData[key] = value;
    localStorage.setItem("newRegisterGuide", JSON.stringify(currentData));
  },
};

const [Provider, useNewRegisterGuideContext] = createSharedContext(
  function useNewRegisterGuideContext() {
    const [guideOptions, setGuideOptions] = useState(
      null as GuideOptions | null
    );

    function hide() {
      setGuideOptions(null);
    }

    function show(
      key: string,
      options:
        | ({
            targetRef: React.RefObject<HTMLElement>;
          } & Omit<GuideOptions, "position">)
        | GuideOptions
    ) {
      if (newRegisterGuideStore.get(key)) {
        return;
      }

      let guideOptions: GuideOptions = {
        position: {
          top: 0,
          left: 0,
        },
      };

      // 如果传入的是 targetRef, 计算 position
      if ("targetRef" in options) {
        const { targetRef, ...restOptions } = options;

        if (targetRef.current) {
          const rect = targetRef.current.getBoundingClientRect();
          const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop;
          const scrollLeft =
            window.pageXOffset || document.documentElement.scrollLeft;

          // 计算目标元素相对于页面的绝对位置
          const targetTop = rect.top + scrollTop;
          const targetLeft = rect.left + scrollLeft;
          const targetWidth = rect.width;
          const targetHeight = rect.height;

          // 根据箭头方向计算Guide组件的位置
          let guideTop = targetTop;
          let guideLeft = targetLeft;

          // 如果有箭头配置，根据箭头方向调整位置
          if (restOptions.arrow) {
            const guideWidth = restOptions.width || 245; // Guide组件的宽度
            const guideHeight = restOptions.height || 80; // Guide组件的高度

            switch (restOptions.arrow) {
              case "left-top":
                guideLeft = targetLeft + targetWidth + 8; // 8px是箭头的大小
                guideTop = targetTop;
                break;
              case "left-bottom":
                guideLeft = targetLeft + targetWidth + 8;
                guideTop = targetTop + targetHeight - guideHeight;
                break;
              case "right-top":
                guideLeft = targetLeft - guideWidth - 8;
                guideTop = targetTop;
                break;
              case "right-bottom":
                guideLeft = targetLeft - guideWidth - 8;
                guideTop = targetTop + targetHeight - guideHeight;
                break;
              case "top-left":
                guideTop = targetTop + targetHeight + 8;
                guideLeft = targetLeft;
                break;
              case "top-right":
                guideTop = targetTop + targetHeight + 8;
                guideLeft = targetLeft + targetWidth - guideWidth;
                break;
              case "bottom-left":
                guideTop = targetTop - guideHeight - 8;
                guideLeft = targetLeft - guideWidth;
                break;
              case "bottom-right":
                guideTop = targetTop - guideHeight - 8;
                guideLeft = targetLeft + targetWidth - guideWidth;
                break;
            }
          } else {
            // 没有箭头时，默认显示在目标元素右侧
            guideLeft = targetLeft + targetWidth + 10;
            guideTop = targetTop;
          }

          guideOptions = {
            ...restOptions,
            position: {
              top: Math.max(0, guideTop), // 确保不超出页面顶部
              left: Math.max(0, guideLeft), // 确保不超出页面左侧
            },
          };
        } else {
          console.warn("targetRef.current is null, using default position");
          guideOptions = {
            ...restOptions,
            position: { top: 0, left: 0 },
          };
        }
      } else {
        // 直接传入 position 的情况
        guideOptions = {
          ...options,
        };
      }

      setGuideOptions(guideOptions);
      newRegisterGuideStore.set(key, Date.now());
    }

    return {
      guideOptions,
      show,
      hide,
    };
  }
);

const GuideWrapper = dynamic(() => import("@/components/Guide/GuideWrapper"), {
  ssr: false,
});
export function GuideProvider({ children }: { children: React.ReactNode }) {
  return (
    <Provider>
      {children}
      <GuideWrapper />
    </Provider>
  );
}

export { useNewRegisterGuideContext as useNewRegisterGuide };

export const clickGuide = () => ({
  position: {
    position: "fixed",
    left: 84,
    height: 76,
    bottom: 20,
  },
  description: (
    <>
      点击任意内容
      <br />
      开启你的个性化灵感推荐之旅
    </>
  ),
  width: 275,
  height: 80,
});

export const organizePinListGuide = (ref: React.RefObject<HTMLElement>) => ({
  targetRef: ref,
  arrow: "top-right",
  description: "批量整理你的采集",
  width: 196,
  height: 55,
});
